import { useRef } from 'react'
import { useI18n } from '@/lib/i18n'
import { BgVideo, PhraseSwap, clamp01, easeInOutCubic, easeOutCubic, lerp, useCtaText, useFrame, usePhraseCycle, type SaverProps } from './shared'

// The SVG works in a 2000-unit square centred on 0,0 and sliced to cover the
// screen, so the shorter screen side always shows at least ±560 units.
const BLADES = 9
const HALF = Math.PI / BLADES
const R_OPEN = 1500 // inradius of the opening when every blade is off screen
const R_MID = 310 // "stopped down" opening that frames the centre
const REACH = 4200 // blade length, far past any screen corner
const RING_R = 548
const CYCLE = 11
// Timeline inside one cycle (seconds). The flash fires at 0 as the iris opens.
const T_OPENED = 0.7
const T_STOP_DOWN = 5.6
const T_STOPPED = 7.0
const T_SNAP = 8.2
const T_CLOSED = 8.32

function apertureAt(c: number) {
  if (c < T_OPENED) return R_OPEN * easeOutCubic(c / T_OPENED)
  if (c < T_STOP_DOWN) return R_OPEN
  if (c < T_STOPPED) return lerp(R_OPEN, R_MID, easeInOutCubic((c - T_STOP_DOWN) / (T_STOPPED - T_STOP_DOWN)))
  if (c < T_SNAP) return R_MID + Math.sin((c - T_STOPPED) * 5) * 10
  if (c < T_CLOSED) return R_MID * (1 - Math.pow((c - T_SNAP) / (T_CLOSED - T_SNAP), 2))
  return 0
}

const TICKS = Array.from({ length: 72 }, (_, i) => i)

export default function ShutterSaver({ videoSrc, phrases, intervalSeconds, wakeMethod }: SaverProps) {
  const { t } = useI18n()
  const cta = useCtaText(wakeMethod)
  const phrase = usePhraseCycle(phrases, Math.max(2.5, intervalSeconds * 1.6))
  const bladeRefs = useRef<(SVGGElement | null)[]>([])
  const rectRefs = useRef<(SVGRectElement | null)[]>([])
  const edgeRefs = useRef<(SVGLineElement | null)[]>([])
  const seamRefs = useRef<(SVGGElement | null)[]>([])
  const bodyRef = useRef<SVGGElement>(null)
  const flashRef = useRef<HTMLDivElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const titleRef = useRef<HTMLDivElement>(null)
  const hudRef = useRef<HTMLDivElement>(null)
  const focusRef = useRef<HTMLDivElement>(null)

  useFrame((time) => {
    // Start just after the flash so the first frame is a calm, open view.
    const c = (time + T_OPENED + 0.4) % CYCLE
    const r = apertureAt(c)
    const closed = 1 - r / R_OPEN
    const twist = closed * 1.15
    const edgeStart = -r * Math.tan(HALF)
    for (let i = 0; i < BLADES; i++) {
      const angle = ((i * 2 * Math.PI) / BLADES + twist) * (180 / Math.PI)
      const transform = `rotate(${angle.toFixed(3)}) translate(0 ${(-r).toFixed(2)})`
      bladeRefs.current[i]?.setAttribute('transform', transform)
      seamRefs.current[i]?.setAttribute('transform', transform)
      rectRefs.current[i]?.setAttribute('x', edgeStart.toFixed(2))
      rectRefs.current[i]?.setAttribute('width', (REACH - edgeStart).toFixed(2))
      edgeRefs.current[i]?.setAttribute('x1', edgeStart.toFixed(2))
    }
    const flash = c < 1.2 ? Math.exp(-c * 6) : 0
    if (flashRef.current) flashRef.current.style.opacity = String(flash * 0.92)
    if (videoRef.current) videoRef.current.style.filter = `brightness(${(0.82 + flash * 0.9).toFixed(3)}) contrast(1.08)`
    if (bodyRef.current) bodyRef.current.style.opacity = String(clamp01(closed * 1.5))
    if (titleRef.current) {
      const k = c > T_CLOSED ? easeOutCubic(clamp01((c - T_CLOSED - 0.1) / 0.45)) : 0
      titleRef.current.style.opacity = String(k)
      titleRef.current.style.transform = `translate(-50%, -50%) scale(${(0.92 + k * 0.08).toFixed(4)})`
      titleRef.current.style.letterSpacing = `${(0.32 - k * 0.2).toFixed(3)}em`
    }
    if (hudRef.current) {
      const k = c < T_STOP_DOWN ? clamp01((c - 0.5) / 0.5) : clamp01(1 - (c - T_STOP_DOWN) / 0.5)
      hudRef.current.style.opacity = String(k)
    }
    if (focusRef.current) {
      // Focus box hunts, then locks green just before the iris stops down.
      const lock = c > 2.4
      const hunt = lock ? 1 : 1.25 + Math.sin(c * 9) * 0.06
      focusRef.current.style.transform = `translate(-50%, -50%) scale(${hunt.toFixed(3)})`
      focusRef.current.style.borderColor = lock ? 'rgba(74, 222, 128, 0.95)' : 'rgba(255,255,255,0.75)'
    }
  })

  return (
    <div className="absolute inset-0 overflow-hidden bg-black" style={{ fontFamily: "'Geist Variable', sans-serif" }}>
      <BgVideo ref={videoRef} src={videoSrc} />

      {/* Viewfinder HUD while the iris is open */}
      <div ref={hudRef} className="pointer-events-none absolute inset-0 text-white" style={{ opacity: 0 }}>
        {[
          'left-[4vmin] top-[4vmin] border-l-2 border-t-2',
          'right-[4vmin] top-[4vmin] border-r-2 border-t-2',
          'left-[4vmin] bottom-[4vmin] border-l-2 border-b-2',
          'right-[4vmin] bottom-[4vmin] border-r-2 border-b-2',
        ].map((pos) => (
          <div key={pos} className={`absolute h-[6vmin] w-[6vmin] border-white/70 ${pos}`} />
        ))}
        <div
          ref={focusRef}
          className="absolute left-1/2 top-1/2 h-[16vmin] w-[16vmin] rounded-[0.6vmin] border-2"
          style={{ boxShadow: '0 0 0 1px rgba(0,0,0,0.25)' }}
        />
        <div className="absolute left-1/2 top-[5.5vmin] flex -translate-x-1/2 items-center gap-[3vmin] font-mono text-[1.8vmin] tracking-[0.2em] text-white/85" style={{ textShadow: '0 1px 4px rgba(0,0,0,0.7)' }}>
          <span>1/125</span><span>F1.4</span><span>ISO 400</span><span className="text-amber-300">AUTO</span>
        </div>
        <div className="absolute inset-x-0 bottom-[15vmin] px-[8vmin] text-center">
          <PhraseSwap
            phrase={phrase}
            className="text-[4.4vmin] font-bold tracking-tight text-white"
            style={{ textShadow: '0 2px 18px rgba(0,0,0,0.75)' }}
          />
        </div>
      </div>

      <svg className="absolute inset-0 h-full w-full" viewBox="-1000 -1000 2000 2000" preserveAspectRatio="xMidYMid slice" aria-hidden>
        <defs>
          <linearGradient id="ss-blade" gradientUnits="userSpaceOnUse" x1="0" y1="0" x2="0" y2="-700">
            <stop offset="0" stopColor="#5b5c63" />
            <stop offset="0.03" stopColor="#2c2d33" />
            <stop offset="0.35" stopColor="#17171b" />
            <stop offset="1" stopColor="#0b0b0d" />
          </linearGradient>
          <radialGradient id="ss-seam" gradientUnits="userSpaceOnUse" cx="0" cy="0" r="1400">
            <stop offset="0" stopColor="rgba(255,255,255,0.55)" />
            <stop offset="0.5" stopColor="rgba(255,255,255,0.22)" />
            <stop offset="1" stopColor="rgba(255,255,255,0.06)" />
          </radialGradient>
          <radialGradient id="ss-sheen" cx="0.38" cy="0.3" r="0.75">
            <stop offset="0" stopColor="rgba(255,255,255,0.10)" />
            <stop offset="0.6" stopColor="rgba(255,255,255,0)" />
          </radialGradient>
          <linearGradient id="ss-ring" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#3a3b42" />
            <stop offset="0.5" stopColor="#15161a" />
            <stop offset="1" stopColor="#2a2b31" />
          </linearGradient>
          <path id="ss-ring-text" d={`M ${-RING_R + 8} 0 A ${RING_R - 8} ${RING_R - 8} 0 1 1 ${RING_R - 8} 0 A ${RING_R - 8} ${RING_R - 8} 0 1 1 ${-RING_R + 8} 0`} />
        </defs>
        {Array.from({ length: BLADES }, (_, i) => (
          <g key={i} ref={(el) => { bladeRefs.current[i] = el }}>
            <rect ref={(el) => { rectRefs.current[i] = el }} y={-REACH} height={REACH} fill="url(#ss-blade)" />
          </g>
        ))}
        {/* Seams on top of every blade: in a real iris each blade overlaps the
            next, so all edges show equally and the closed iris reads as a pinwheel. */}
        {Array.from({ length: BLADES }, (_, i) => (
          <g key={i} ref={(el) => { seamRefs.current[i] = el }}>
            <line ref={(el) => { edgeRefs.current[i] = el }} x2={REACH} y1={0} y2={0} stroke="url(#ss-seam)" strokeWidth={2} />
          </g>
        ))}
        {/* Lens barrel and camera body, faded in as the iris closes */}
        <g ref={bodyRef} style={{ opacity: 0 }}>
          <circle r={1400} fill="url(#ss-sheen)" />
          <path fillRule="evenodd" fill="#08080a" d={`M -3000 -3000 H 3000 V 3000 H -3000 Z M ${RING_R + 34} 0 A ${RING_R + 34} ${RING_R + 34} 0 1 0 ${-RING_R - 34} 0 A ${RING_R + 34} ${RING_R + 34} 0 1 0 ${RING_R + 34} 0 Z`} />
          <circle r={RING_R + 12} fill="none" stroke="url(#ss-ring)" strokeWidth={46} />
          <circle r={RING_R - 11} fill="none" stroke="rgba(255,255,255,0.18)" strokeWidth={1.5} />
          <circle r={RING_R + 35} fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth={2} />
          {TICKS.map((i) => (
            <line
              key={i}
              x1={0} x2={0}
              y1={-(RING_R + 26)}
              y2={-(RING_R + (i % 6 === 0 ? 4 : 16))}
              stroke={i % 6 === 0 ? 'rgba(255,255,255,0.55)' : 'rgba(255,255,255,0.22)'}
              strokeWidth={i % 6 === 0 ? 2.5 : 1.5}
              transform={`rotate(${i * 5})`}
            />
          ))}
          <text fill="rgba(255,255,255,0.55)" fontSize={17} letterSpacing={6} fontFamily="'Geist Variable', sans-serif" fontWeight={600}>
            <textPath href="#ss-ring-text" startOffset="2%">POSEBOX PHOTO LENS · 50mm 1:1.4 · ∅62</textPath>
          </text>
          <circle cx={RING_R * 0.7} cy={-RING_R * 0.7} r={9} fill="#e11d48" />
        </g>
      </svg>

      <div
        ref={titleRef}
        className="pointer-events-none absolute left-1/2 top-1/2 w-[90vw] text-center text-[9vmin] font-extrabold uppercase leading-none text-white"
        style={{ opacity: 0, textShadow: '0 4px 30px rgba(0,0,0,0.9)' }}
      >
        {t('screensaver.hero.shutter')}
      </div>

      <div className="pointer-events-none absolute inset-x-0 bottom-[5vmin] flex justify-center">
        <div className="flex items-center gap-[1.4vmin] rounded-full border border-white/25 bg-black/45 px-[3vmin] py-[1.4vmin] text-[2.4vmin] font-semibold tracking-wide text-white backdrop-blur-md">
          <span className="relative flex h-[1.4vmin] w-[1.4vmin]">
            <span className="absolute inset-0 animate-ping rounded-full bg-white/70" />
            <span className="relative h-full w-full rounded-full bg-white" />
          </span>
          {cta}
        </div>
      </div>

      <div ref={flashRef} className="pointer-events-none absolute inset-0 bg-white" style={{ opacity: 0 }} />
    </div>
  )
}
